import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Button } from "primereact/button";
import { HitgenPDFLink, XchemPDFLink } from "../terms_and_conditions/ReadPdf";
import { ProgressSpinner } from "primereact/progressspinner";
import VersionDialog from "./VersionDetails";
import debounce from "lodash.debounce";
import Swal from "sweetalert2";

// Constants
const DEBOUNCE_DELAY = 300;
const DEFAULT_ROWS_PER_PAGE = 20;
const ROWS_PER_PAGE_OPTIONS = [20, 50, 100, 200];

// Partner color mapping
const PARTNER_COLORS = {
  "X-Chem": "bg-[#3b82f6]/30",
  HitGen: "bg-[#10b981]/30",
  UNC: "bg-[#f59e0b]/30",
  External: "bg-[#cd49cd]/20",
  default: "bg-gray-100",
};

// Utility functions
const showErrorAlert = (title, text, callback) => {
  Swal.fire({
    title,
    text,
    icon: "error",
    confirmButtonText: "OK",
    allowOutsideClick: false,
    customClass: {
      confirmButton:
        "bg-blue-500 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded",
    },
  }).then((result) => {
    if (result.isConfirmed && callback) {
      callback();
    }
  });
};

const navigateToDatasets = () => {
  window.location.href = "/datasets";
};

const renderDictItem = ({ name, entry }) => (
  <ul
    className="p-2 sm:p-3 flex items-center text-gray-800 border border-gray-400"
    key={name}
  >
    <li className="w-full text-left ml-2">{name}</li>
    <li className="w-full text-left">{entry.mode}</li>
    <li className="w-full text-left">{entry.type}</li>
    <li className="w-full text-left">{entry.description}</li>
  </ul>
);

const useFilteredData = (data, searchQuery) => {
  const [filteredData, setFilteredData] = useState([]);

  const debouncedSearch = useMemo(
    () =>
      debounce((term) => {
        if (!data) return;

        if (!term?.trim()) {
          setFilteredData(data);
          return;
        }

        const lowerTerm = term.toLowerCase().trim();
        const results = data.filter(
          (item) =>
            item.target?.toLowerCase().includes(lowerTerm) ||
            item.description?.toLowerCase().includes(lowerTerm) ||
            item.partner?.toLowerCase().includes(lowerTerm)
        );

        setFilteredData(results);
      }, DEBOUNCE_DELAY),
    [data]
  );

  useEffect(() => {
    if (data) {
      setFilteredData(data);
    }
  }, [data]);

  useEffect(() => {
    debouncedSearch(searchQuery);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchQuery, debouncedSearch]);

  return filteredData;
};

const Table = React.memo(({ datasets, dict, searchQuery }) => {
  const [dictDialogInfo, setDictDialogInfo] = useState({
    visible: false,
    partner: null,
  });
  const [applicationId, setApplicationId] = useState(null);
  const [hitgenAccepted, setHitgenAccepted] = useState(false);
  const [hitgenTerms, setHitgenTerms] = useState(false);
  const [xchemAccepted, setXchemAccepted] = useState(false);
  const [xchemTerms, setXchemTerms] = useState(false);
  const [preApproved, setPreApproved] = useState(false);
  const { currentUser } = useAuth();
  const [visibleConfirm, setVisibleConfirm] = useState(false);
  const [isAgreed, setAgreeTerms] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [downloading, setDownloading] = useState({});

  const [selectedOption, setSelectedOption] = useState("Version 1");
  const filteredData = useFilteredData(datasets, searchQuery);

  useEffect(() => {
    const approvalCheck = debounce(async () => {
      try {
        const res = await axios.get(
          `/api/data/getwhitelisted/${currentUser.email}`
        );
        setHitgenAccepted(res.data.hitgenAccepted);
        setHitgenTerms(res.data.hitgenTerms);
        setXchemAccepted(res.data.xchemAccepted);
        setXchemTerms(res.data.xchemTerms);
        setPreApproved(res.data.preApproved);
      } catch (error) {
        console.error("Error fetching approval status: ", error);
      }
    }, 300);

    const getApplication = debounce(async () => {
      try {
        const res = await axios.get(
          `/api/application/email/${currentUser.email}`
        );
        setApplicationId(res.data.applicationId);
      } catch (error) {
        console.error("Error fetching application: ", error);
      }
    }, 300);

    if (currentUser) {
      approvalCheck();
      getApplication();
    }

    return () => {
      approvalCheck.cancel();
      getApplication.cancel();
    };
  }, [currentUser]);

  const handleDownloadClick = useCallback((rowData) => {
    setSelectedRowData(rowData);
    setVisibleConfirm(true);
  }, []);

  const handleDownload = useCallback(async (datasetId, partner) => {
    if (!datasetId || !partner) {
      console.error("Missing datasetId or partner");
      return;
    }

    try {
      setDownloading((prev) => ({ ...prev, [datasetId]: true }));

      const response = await axios.get(`/api/gcp/getsignedurl`, {
        params: { datasetId, partner },
        timeout: 30000, // 30 second timeout
      });

      // More robust file checking with better error handling
      try {
        const checkResponse = await fetch(
          `/api/gcp/check_file?url=${encodeURIComponent(
            response.data.signedUrl
          )}`
        );

        // Check if the response is actually successful
        if (!checkResponse.ok) {
          console.error(
            `File check failed with status: ${checkResponse.status}`
          );
          showErrorAlert(
            "Service Unavailable",
            "Unable to verify file availability. Please try again later.",
            navigateToDatasets
          );
          return;
        }

        // Check content type to ensure we're getting JSON
        const contentType = checkResponse.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          console.error("Server returned non-JSON response:", contentType);
          showErrorAlert(
            "Service Error",
            "Server returned an unexpected response. Please try again later.",
            navigateToDatasets
          );
          return;
        }

        const checkData = await checkResponse.json();

        if (checkData.exists) {
          window.location.assign(response.data.signedUrl);
        } else {
          showErrorAlert(
            "File Not Found",
            "The requested file could not be found. Please try again later.",
            navigateToDatasets
          );
        }
      } catch (fetchError) {
        console.error("File check error:", fetchError);
        showErrorAlert(
          "File Check Failed",
          "Unable to verify file availability. Please try again later.",
          navigateToDatasets
        );
      }
    } catch (error) {
      console.error("Download error:", error);

      let errorMessage = "Error fetching download link. Please try again.";

      if (error.response?.status === 429) {
        errorMessage =
          error.response.data.message ||
          "Too many requests, please try again later.";
      } else if (error.code === "ECONNABORTED") {
        errorMessage = "Request timeout. Please try again.";
      } else if (error.request) {
        errorMessage = "No response from the server. Please try again later.";
      }

      alert(errorMessage);
    } finally {
      setDownloading((prev) => ({ ...prev, [datasetId]: false }));
    }
  }, []);

  const handleYesClick = useCallback(() => {
    setVisibleConfirm(false);
    if (selectedRowData?.link && selectedRowData?.partner) {
      handleDownload(selectedRowData.link, selectedRowData.partner);
    }
  }, [handleDownload, selectedRowData]);

  const handleHistoryClick = useCallback((rowData) => {
    setSelectedRow(rowData);
    setHistoryDialog(true);
  }, []);

  const renderDataDictionaryButton = useCallback((rowData) => {
    const updatedPartner =
      rowData.dataType && rowData.partner === "HitGen"
        ? "HitGen-Updated"
        : rowData.partner;

    return (
      <button
        className="text-blue-700 py-2 hover:text-blue-500 hover:scale-105 transition duration-100"
        onClick={() =>
          setDictDialogInfo({ visible: true, partner: updatedPartner })
        }
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-8 h-8"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
          />
        </svg>
      </button>
    );
  }, []);

  const renderHistory = useCallback(
    (rowData) => {
      const isApprovedForDownload =
        rowData.partner === "X-Chem" ? xchemAccepted && xchemTerms : true;

      if (isApprovedForDownload) {
        return (
          <button
            className="text-blue-700 py-2 hover:text-blue-500 hover:scale-105 transition duration-100"
            onClick={() => handleHistoryClick(rowData)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-8 h-8"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
              />
            </svg>
          </button>
        );
      } else {
        return (
          <Button
            type="button"
            label="Not Authorized"
            tooltip="You need to apply for access to this dataset. Once accepted, you will be able to view it."
            tooltipOptions={{
              position: "bottom",
              mouseTrack: true,
              mouseTrackTop: 15,
            }}
          />
        );
      }
    },
    [xchemAccepted, xchemTerms, handleHistoryClick]
  );

  const renderDownloadButton = useCallback(
    (rowData) => {
      const isApprovedForDownload =
        rowData.partner === "X-Chem" ? xchemAccepted && xchemTerms : true;
      const isDownloading = downloading[rowData.link];

      if (isApprovedForDownload) {
        return (
          <>
            {!isAgreed && (
              <button
                className="text-blue-700 py-2 hover:text-blue-500 hover:scale-105 transition duration-100 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleDownloadClick(rowData)}
                disabled={isDownloading}
                aria-label={
                  isDownloading ? "Downloading..." : "Download dataset"
                }
              >
                {isDownloading ? (
                  <ProgressSpinner style={{ width: "24px", height: "24px" }} />
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9.75v6.75m0 0-3-3m3 3 3-3m-8.25 6a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z"
                    />
                  </svg>
                )}
              </button>
            )}
          </>
        );
      } else if (currentUser) {
        if (applicationId) {
          if (rowData.partner === "X-Chem") {
            if (!xchemAccepted) {
              return (
                <span className="text-red-500 text-xs md:text-sm">{`Denied access for ${rowData.partner} datasets`}</span>
              );
            } else if (!xchemTerms) {
              return (
                <span className="text-red-500 text-xs md:text-sm">{`Your application has not agreed to ${rowData.partner} EULA`}</span>
              );
            }
          } else if (rowData.partner === "HitGen") {
            if (!hitgenAccepted) {
              return (
                <span className="text-red-500 text-xs md:text-sm">{`Denied access for ${rowData.partner} datasets`}</span>
              );
            } else if (!hitgenTerms) {
              return (
                <span className="text-red-500 text-xs md:text-sm">{`Your application has not agreed to ${rowData.partner} EULA`}</span>
              );
            }
          }
          return (
            <span className="text-blue-700 text-xs md:text-sm">
              Pending approval
            </span>
          );
        } else {
          return (
            <Link to="/application">
              <button className="text-blue-700 py-2 hover:text-blue-500 hover:scale-105 transition duration-100">
                Apply for X-Chem dataset
              </button>
            </Link>
          );
        }
      } else {
        return (
          <span className="text-red-500 text-xs md:text-sm">
            Please login/signup to access the XChem datasets
          </span>
        );
      }
    },
    [
      hitgenAccepted,
      hitgenTerms,
      xchemAccepted,
      xchemTerms,
      currentUser,
      applicationId,
      isAgreed,
      downloading,
      handleDownloadClick,
    ]
  );

  // const getRowClassName = useCallback((data) => {
  //   const partner = data[0]?.partner;

  //   return PARTNER_COLORS[partner] || PARTNER_COLORS.default;
  // }, []);

  const getRowClassName = useCallback((data) => {
    // Handle both array and object formats
    const partner = Array.isArray(data) ? data[0]?.partner : data?.partner;

    return PARTNER_COLORS[partner] || PARTNER_COLORS.default;
  }, []);

  return (
    <div className="w-full bg-white">
      <Dialog
        visible={visibleConfirm}
        header="Download Dataset"
        style={{ width: "700px" }}
        onHide={() => setVisibleConfirm(false)}
        footer={
          <div>
            <Button
              label="Cancel"
              onClick={() => setVisibleConfirm(false)}
              className="w-full inline-flex justify-center rounded-md shadow-sm px-4 py-2 bg-red-500 text-base text-white hover:bg-red-700 sm:ml-3 sm:w-auto sm:text-sm"
            />
            <Button
              className="mt-3 w-full inline-flex justify-center rounded-md shadow-sm px-4 py-2 bg-green-500 text-base text-white hover:bg-green-700 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              label="Accept & Download"
              onClick={() => handleYesClick(selectedRowData)}
            />
          </div>
        }
      >
        {selectedRowData && (
          <div className="space-y-4">
            <p className="text-gray-700">
              You are about to download:{" "}
              <strong>{selectedRowData.target}</strong>
            </p>
            <p className="text-sm text-gray-600">
              {selectedRowData.partner === "HitGen" ? (
                <>
                  By clicking <b>Accept & Download</b>, you acknowledge and
                  agree that your access to and use of this Dataset is subject
                  to the terms and condition of the <HitgenPDFLink /> ,
                  including a strict prohibition on reverse engineering the
                  identities of structures or other attributes of the
                  DNA-encoded libraries from which the Dataset is derived
                </>
              ) : selectedRowData.partner === "X-Chem" ? (
                <>
                  By clicking <b>Accept & Download</b>, you acknowledge and
                  agree that your access to and use of this Dataset is subject
                  to the terms and condition of the <XchemPDFLink /> , including
                  a strict prohibition on reverse engineering the identities of
                  structures or other attributes of the DNA-encoded libraries
                  from which the Dataset is derived
                </>
              ) : selectedRowData.partner === "External" ? (
                <>
                  By clicking <b>Accept & Download</b>, you agree to be
                  redirected to the dataset hosted on Kaggle. Please note that
                  this dataset is not an asset of the AIRCHECK project, and you
                  must comply with the terms and conditions set by the
                  competition
                </>
              ) : selectedRowData.partner === "UNC" ? (
                <>
                  By clicking <b>Accept & Download</b>, you acknowledge and
                  agree that your access to and use of this Dataset is subject
                  to the terms and condition of the <HitgenPDFLink /> ,
                  including a strict prohibition on reverse engineering the
                  identities of structures or other attributes of the
                  DNA-encoded libraries from which the Dataset is derived
                </>
              ) : null}
            </p>
          </div>
        )}
      </Dialog>

      <DataTable
        className="centered-header"
        value={filteredData}
        stripedRows
        paginator
        rows={DEFAULT_ROWS_PER_PAGE}
        sortField="loginRequired"
        sortOrder={1}
        rowClassName={getRowClassName}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      >
        <Column
          field="target"
          header="Target Name"
          style={{ width: "15%" }}
          alignHeader={"center"}
          sortable
        />
        <Column
          field="description"
          header="Description"
          style={{ width: "20%" }}
          alignHeader={"center"}
        />
        <Column
          field="selectionDate"
          header="Selection Date"
          style={{ width: "15%" }}
          alignHeader={"center"}
          sortable
        />
        <Column
          field="partner"
          header="Partner"
          style={{ width: "12.5%" }}
          alignHeader={"center"}
          sortable
        />
        <Column
          body={renderDataDictionaryButton}
          style={{ width: "12.5%" }}
          alignHeader={"center"}
          header="Data Dictionary"
        />
        <Column
          body={renderDownloadButton}
          style={{ width: "15%" }}
          alignHeader={"center"}
          header="Download Dataset"
          field="isDownloadable"
        />
        <Column
          body={renderHistory}
          style={{ width: "15%" }}
          alignHeader={"center"}
          header="Dataset History"
        />
      </DataTable>

      <Dialog
        visible={dictDialogInfo.visible}
        onHide={() => setDictDialogInfo({ visible: false, partner: null })}
        header="Data Dictionary"
      >
        <ul className="bg-gray-200 p-2 sm:p-3 flex items-center text-gray-800 border border-gray-400">
          <li className="w-full text-left font-bold mr-4">Field Name</li>
          <li className="w-full text-left font-bold">Data Type</li>
          <li className="w-full text-left font-bold"> Mode</li>
          <li className="w-full text-left font-bold">Description</li>
        </ul>
        {dictDialogInfo.visible &&
          dict[dictDialogInfo.partner] &&
          Object.entries(dict[dictDialogInfo.partner])
            .filter(([key]) => key !== "id")
            .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
            .map(([name, entry]) => renderDictItem({ name, entry }))}
      </Dialog>

      <VersionDialog
        header="Dataset History"
        visible={historyDialog}
        maximizable
        style={{ width: "40vw" }}
        onHide={() => {
          setHistoryDialog(false);
          setSelectedRowData(null);
          setSelectedOption("Version 1");
        }}
        approvedDownload={{
          hitgen: hitgenAccepted,
          hitgenTerms,
          xchem: xchemAccepted,
          xchemTerms,
          preApproved,
        }}
        rowData={selectedRow}
        dataDictionary={dict}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
      />
    </div>
  );
});

export default Table;
